import { buildTable, isUpdatingFilters } from "../components/features/app-table/builder/tableBuilder.js";
import { switchSidebarTool } from "../components/layout/app-sidebar/utils/sidebarManager.js";
import { hasPermission } from "./auth.js";
import { mountCustomersView } from "./views/customersView.js";
import { mountUsersView } from "./views/usersView.js";
import { getCurrentDialogName } from "../components/features/app-dialog/utils/core/dialogManager.js";
import { setPinnedFilterLabels, updateFilterOptions, setSuppressPinInvalidation } from "../components/features/app-table/utils/filterManager.js";

export let currentView = null;
export let currentTable = null;
export let currentTableName = null;

let setViewToken = 0;
const dateFilters = {};
export const getDateFilter = (view) => dateFilters[view] ?? null;

const viewLabels = {
    Time: 'Zeiterfassung', Timelogs: 'Arbeitstage', Entries: 'Leistungsnachweise',
    Projects: 'Projekte', Teams: 'Teams', Users: 'Nutzverwaltung',
    Roles: 'Rollen', Customers: 'Kundenverwaltung', Applications: 'Applikationen',
    Services: 'Services',
};
const filterPerms = {
    Timelogs: { user: "Timelogs.Read.Own", team: "Timelogs.Read.Team", all: "Timelogs.Read.Any" },
    Entries:  { user: "Entries.Read.Own",  team: "Entries.Read.Team",  all: "Entries.Read.Any"  },
};
const panelViews = {
    Customers: { mount: mountCustomersView, sidebar: 'toolCustomers',                    tableName: 'customer' },
    Users:     { mount: mountUsersView,     sidebar: 'toolUsers', sidebarGroup: 'users', tableName: 'user'     },
};

function parseHash(hash) {
    const clean = hash.replace(/^#\/?/, '');
    const questionIdx = clean.indexOf('?');
    const pathPart = questionIdx >= 0 ? clean.slice(0, questionIdx) : clean;
    const queryStr = questionIdx >= 0 ? clean.slice(questionIdx + 1) : '';
    const [segment = '', id = null] = pathPart.split('/');
    const lower = segment.toLowerCase();
    const view = lower ? lower.charAt(0).toUpperCase() + lower.slice(1) : 'Time';
    return { view, id, filterParams: queryStr ? new URLSearchParams(queryStr) : null };
}
function buildViewHash(view, filterValue) {
    const params = new URLSearchParams();
    if (filterPerms[view]) params.set('scope', filterValue || 'user');
    const filters = dateFilters[view];
    if (filters?.period) params.set('period', filters.period);
    if (filters?.mode === 'coarse') {
        if (filters.year) params.set('year', filters.year);
        if (filters.months?.[0]) params.set('month', filters.months[0]);
    } else if (filters?.mode === 'exact') {
        if (filters.from) params.set('from', filters.from);
        if (filters.to) params.set('to', filters.to);
    }
    if (currentTable) {
        currentTable.getHeaderFilters().forEach(({ field, value }) => {
            if (value !== '' && value !== null && value !== undefined) params.set(`col-${field}`, value);
        });
    }
    const currentDialog = getCurrentDialogName();
    if (currentDialog) params.set('dialog', currentDialog);
    return `#${view.toLowerCase()}?${params.toString()}`;
}
function appendFilter(container, perms, view, filterParams) {
    const filter = document.createElement("app-filter");
    Object.entries(perms).forEach(([k, v]) => filter.setAttribute(`perm-${k}`, v));
    const urlScope = filterParams?.get('scope');
    if (urlScope) sessionStorage.setItem(`filter${view}`, urlScope);
    const saved = urlScope || sessionStorage.getItem(`filter${view}`);
    if (saved) filter.setAttribute("selected", saved);
    filter.addEventListener("filter-change", e => {
        sessionStorage.setItem(`filter${view}`, e.detail.value);
        history.replaceState({ view }, '', buildViewHash(view, filter.value));
    });
    filter.addEventListener("date-filter-change", e => {
        dateFilters[view] = e.detail;
        history.replaceState({ view }, '', buildViewHash(view, filter.value));
    });
    container.appendChild(filter);
    return filter;
}
function applyUrlDateFilter(view, filterParams) {
    if (!filterParams) return;
    const year = filterParams.get('year'), month = filterParams.get('month');
    const from = filterParams.get('from'), to = filterParams.get('to');
    const period = filterParams.get('period') || undefined;
    if (from || to) {
        dateFilters[view] = { mode: 'exact', from: from || null, to: to || null, period };
    } else if (year && month) {
        const monthNum = parseInt(month, 10);
        dateFilters[view] = {
            mode: 'coarse', months: [month], year, period,
            from: `${year}-${month.padStart(2, '0')}-01`,
            to:   `${year}-${month.padStart(2, '0')}-${String(new Date(Number(year), monthNum, 0).getDate()).padStart(2, '0')}`,
        };
    } else if (year) {
        dateFilters[view] = { mode: 'coarse', months: [], year, period };
    }
}
function attachColFilterSync(table, filter, view, filterParams, token) {
    const colFilters = {};
    const colLabels = {};
    if (filterParams) {
        for (const [key, value] of filterParams.entries()) {
            if (key.startsWith('col-')) colFilters[key.slice(4)] = value;
            else if (key.startsWith('colName-')) colLabels[key.slice(8)] = value;
        }
    }

    const pins = {};
    Object.entries(colFilters).forEach(([field, value]) => {
        if (!colLabels[field]) return;
        const num = Number(value);
        pins[field] = { value: !isNaN(num) && value !== '' ? num : value, label: colLabels[field] };
    });
    setPinnedFilterLabels(pins);

    if (Object.keys(pins).length > 0) setSuppressPinInvalidation(true);

    let applied = false, applying = false;
    table.on('dataFiltered', () => {
        if (token !== setViewToken || applying) return;
        if (!applied) {
            applied = true;
            if (Object.keys(colFilters).length === 0) return;
            applying = true;
            Object.entries(colFilters).forEach(([field, value]) => {
                const num = Number(value);
                table.setHeaderFilterValue(field, !isNaN(num) && value !== '' ? num : value);
            });
            applying = false;
            const rebuild = () => {
                if (token !== setViewToken) return;
                updateFilterOptions(table.getRows('active').map(row => row.getData()), table);
            };
            const delays = [0, 50, 200, 600];
            delays.forEach((delay, i) => setTimeout(() => {
                rebuild();
                if (i === delays.length - 1) setSuppressPinInvalidation(false);
            }, delay));
            return;
        }
        if (isUpdatingFilters) return;
        history.replaceState({ view }, '', buildViewHash(view, filter?.value));
    });
}

export async function setView(view, pushState = true, { selectedId = null, filterParams = null } = {}) {
    if (!hasPermission(`${view}.Read`) && !hasPermission(`${view}.Read.Own`) && !hasPermission(`${view}.Read.Team`) && !hasPermission(`${view}.Read.Any`) && view !== "Time" && view !== "Timelogs") return;

    const token = ++setViewToken;
    if (pushState && window.location.hash !== `#${view.toLowerCase()}`) history.pushState({ view }, '', `#${view.toLowerCase()}`);

    if (!filterParams?.get('dialog')) document.querySelector("app-dialog")?.close();
    const content = document.querySelector("#content");
    if (!content) return;
    content.replaceChildren();

    const panel = panelViews[view];
    if (panel) {
        await panel.mount(content, { selectedId });
        switchSidebarTool(panel.sidebar, panel.sidebarGroup);
        document.querySelector('app-header')?.setPageTitle(viewLabels[view] ?? view);
        currentView = view; currentTable = null; currentTableName = panel.tableName;
        const panelDialog = filterParams?.get('dialog');
        if (panelDialog) window.dispatchEvent(new CustomEvent('restore-dialog', { detail: { name: panelDialog, filterParams } }));
        return;
    }

    if (filterPerms[view]) {
        delete dateFilters[view];
        applyUrlDateFilter(view, filterParams);
    }

    let appTimeReady = Promise.resolve(), appSummaryReady = Promise.resolve();
    if (view === "Time") {
        const appTime = document.createElement("app-time");
        content.appendChild(appTime);
        appTimeReady = appTime.ready;
    }
    if (view === "Entries" || view === "Timelogs") {
        const appSummary = document.createElement("app-summary");
        appSummary.setAttribute("type", view.toLowerCase());
        content.appendChild(appSummary);
        appSummaryReady = appSummary.ready;

        const summaryDivider = document.createElement("div");
        summaryDivider.className = "summary-divider";
        content.appendChild(summaryDivider);
    }

    const filter = filterPerms[view] ? appendFilter(content, filterPerms[view], view, filterParams) : null;
    const [table] = await Promise.all([buildTable(view), appTimeReady, appSummaryReady, filter?.ready ?? Promise.resolve()]);

    if (token !== setViewToken) return;

    if (filter && filterParams) {
        const from = filterParams.get('from'), to = filterParams.get('to');
        const year = filterParams.get('year'), month = filterParams.get('month');
        const period = filterParams.get('period');
        if (period || from || to || year || month) filter.setDateFilter({ period, from, to, year, month });
    }

    currentTable = table;
    currentTableName = null;
    if (table) attachColFilterSync(table, filter, view, filterParams, token);

    for (const element of content.children) { element.style.transition = "opacity 0.15s ease-in"; element.style.opacity = "1"; }

    const adminViews = ['Projects', 'Teams', 'Roles', 'Applications', 'Services'];
    switchSidebarTool(adminViews.includes(view) ? 'toolAdmin' : `tool${view}`, view.toLowerCase());
    document.querySelector('app-header')?.setPageTitle(viewLabels[view] ?? view);
    currentView = view;
    const tableDialog = filterParams?.get('dialog');
    if (tableDialog) window.dispatchEvent(new CustomEvent('restore-dialog', { detail: { name: tableDialog, filterParams } }));
}

const navigate = () => {
    const { view, id, filterParams } = parseHash(window.location.hash);
    setView(view, false, { selectedId: id, filterParams });
};
export const checkCurrentView = navigate;
window.addEventListener('hashchange', navigate);